import * as XLSX from 'xlsx';
import Formatter from '../scripts/formatters';

const fechaApiToKey = (value) => {
  if (value == null || value === '') return null;
  const match = String(value).match(/^(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : null;
};

export const mesesEnPeriodo = (fechaInicio, fechaFin) => {
  const iniKey = fechaApiToKey(fechaInicio);
  const finKey = fechaApiToKey(fechaFin);
  if (!iniKey || !finKey) return [];

  const meses = [];
  let cur = new Date(`${iniKey}T12:00:00`);
  const fin = new Date(`${finKey}T12:00:00`);
  cur = new Date(cur.getFullYear(), cur.getMonth(), 1);
  const finMes = new Date(fin.getFullYear(), fin.getMonth(), 1);

  while (cur <= finMes) {
    const key = `${cur.getFullYear()}-${String(cur.getMonth() + 1).padStart(2, '0')}`;
    meses.push({
      key,
      label: cur.toLocaleDateString('es-ES', { month: 'short' }).replace('.', '').toUpperCase(),
      year: cur.getFullYear(),
    });
    cur = new Date(cur.getFullYear(), cur.getMonth() + 1, 1);
  }
  return meses;
};

const equivalenteDia = (tipo) => (tipo === 'C' ? 1 : 0.5);

const etiquetaDia = (fecha, tipo) => {
  const key = fechaApiToKey(fecha);
  if (!key) return '';
  const dia = key.slice(8, 10);
  const suf = tipo === 'C' ? '1d' : '0.5d';
  return `${dia} ${suf}`;
};

const round2 = (value) => Math.round((Number(value) || 0) * 100) / 100;

export const construirFilasReporte = (empleados, dias, meses) => {
  const diasPorEmpleado = {};
  (dias || []).forEach((d) => {
    const cod = d.CodEmpleado;
    if (!diasPorEmpleado[cod]) diasPorEmpleado[cod] = [];
    diasPorEmpleado[cod].push(d);
  });

  return (empleados || []).map((emp) => {
    const lista = (diasPorEmpleado[emp.CodEmpleado] || []).slice().sort((a, b) => {
      const fa = fechaApiToKey(a.Fecha) || '';
      const fb = fechaApiToKey(b.Fecha) || '';
      return fa.localeCompare(fb);
    });

    const porMes = {};
    meses.forEach((m) => {
      porMes[m.key] = { fechas: [], total: 0 };
    });

    lista.forEach((d) => {
      const key = fechaApiToKey(d.Fecha);
      if (!key) return;
      const mesKey = key.slice(0, 7);
      if (!porMes[mesKey]) return;
      porMes[mesKey].fechas.push(etiquetaDia(d.Fecha, d.TipoJornada));
      porMes[mesKey].total += equivalenteDia(d.TipoJornada);
    });

    const totalDescansadosRaw =
      Number(emp.TotalDiasDescansados ?? emp.TotalDiasEquivalentes) ||
      lista.reduce((acc, d) => acc + equivalenteDia(d.TipoJornada), 0);

    const saldoInicial = round2(emp.SaldoInicial);
    const diasAcumular = round2(emp.DiasAcumular);
    const totalDescansados = round2(totalDescansadosRaw);
    const saldoFinal = round2(saldoInicial + diasAcumular - totalDescansados);

    return {
      ...emp,
      porMes,
      saldoInicial,
      diasAcumular,
      totalDescansados,
      saldoFinal,
    };
  });
};

export const exportReporteDepartamentoExcel = (reporte) => {
  const encabezado = reporte?.encabezado || {};
  const meses = mesesEnPeriodo(encabezado.FechaInicio, encabezado.FechaFin);
  const filas = construirFilasReporte(reporte?.empleados, reporte?.dias, meses);

  if (!filas.length) {
    throw new Error('No hay datos para exportar');
  }

  const deptNombre = (encabezado.NombreDepartamento || 'DEPARTAMENTO').toUpperCase();
  const periodoNombre = encabezado.NombrePeriodo || '';
  const fechaIni = encabezado.FechaInicio ? Formatter.dateFormatter(encabezado.FechaInicio) : '';
  const fechaFin = encabezado.FechaFin ? Formatter.dateFormatter(encabezado.FechaFin) : '';

  const fixedCols = 3;
  const tailCols = 3;
  const totalCols = fixedCols + meses.length * 2 + tailCols;

  const wsData = [
    [`PROGRAMACIÓN PLAN DE VACACIONES — ${deptNombre}`],
    [
      periodoNombre
      + (fechaIni && fechaFin ? ` · ${fechaIni} — ${fechaFin}` : ''),
    ],
    [],
  ];

  const header1 = ['Nombre', 'Cargo', 'Saldo ini.'];
  meses.forEach((m) => {
    header1.push(m.label, '');
  });
  header1.push('Días acum.', 'Total desc.', 'Saldo fin.');
  wsData.push(header1);

  const header2 = ['', '', ''];
  meses.forEach(() => {
    header2.push('Fechas', 'Días');
  });
  header2.push('', '', '');
  wsData.push(header2);

  filas.forEach((row) => {
    const dataRow = [
      row.NombreEmpleado,
      row.Cargo || '',
      row.saldoInicial,
    ];
    meses.forEach((m) => {
      const cel = row.porMes[m.key];
      dataRow.push(
        cel?.fechas?.length ? cel.fechas.join('\n') : '',
        cel?.total ? cel.total : ''
      );
    });
    dataRow.push(row.diasAcumular, row.totalDescansados, row.saldoFinal);
    wsData.push(dataRow);
  });

  wsData.push([]);
  wsData.push([
    'Entregado por:',
    encabezado.JefeEntrega || '',
    encabezado.JefeEntregaCargo || '',
    '',
    '',
    'Recibido por:',
    encabezado.JefeRecibe || '',
    encabezado.JefeRecibeCargo || 'Recursos Humanos',
  ]);

  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.aoa_to_sheet(wsData);

  const merges = [
    { s: { r: 0, c: 0 }, e: { r: 0, c: totalCols - 1 } },
    { s: { r: 1, c: 0 }, e: { r: 1, c: totalCols - 1 } },
  ];

  meses.forEach((_, i) => {
    const col = fixedCols + i * 2;
    merges.push({ s: { r: 3, c: col }, e: { r: 3, c: col + 1 } });
  });

  for (let c = 0; c < fixedCols; c += 1) {
    merges.push({ s: { r: 3, c }, e: { r: 4, c } });
  }
  for (let c = fixedCols + meses.length * 2; c < totalCols; c += 1) {
    merges.push({ s: { r: 3, c }, e: { r: 4, c } });
  }

  ws['!merges'] = merges;

  const colWidths = [
    { wch: 32 },
    { wch: 22 },
    { wch: 10 },
  ];
  meses.forEach(() => {
    colWidths.push({ wch: 18 }, { wch: 8 });
  });
  colWidths.push({ wch: 10 }, { wch: 12 }, { wch: 10 });
  ws['!cols'] = colWidths;

  XLSX.utils.book_append_sheet(wb, ws, 'Plan vacaciones');

  const deptSlug = String(encabezado.NombreDepartamento || 'depto')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9]+/g, '_')
    .replace(/^_|_$/g, '')
    .slice(0, 24) || 'depto';
  const fecha = new Date().toISOString().split('T')[0];
  XLSX.writeFile(wb, `Plan_Vacaciones_${deptSlug}_${fecha}.xlsx`);
};
